import { Link } from 'react-router-dom';
import { Sun, Moon, Monitor, Contrast, Zap, Type } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Switch } from './ui/switch';

const THEMES = [
  { value: 'light', label: 'Claro', Icon: Sun },
  { value: 'dark', label: 'Oscuro', Icon: Moon },
  { value: 'system', label: 'Automático', Icon: Monitor },
];

function Row({ id, Icon, title, desc, checked, onChange }) {
  return (
    <div className="flex items-center gap-3 py-3">
      <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
        <Icon size={18} className="text-torre-slate" aria-hidden="true" />
      </div>
      {/* The label forwards clicks (a button is labelable) but does NOT name it: HTML-AAM gives a
          button its name from its contents, never from a <label>, so all three switches announced
          as just "switch, not pressed". Name them explicitly. */}
      <label htmlFor={id} className="flex-1 min-w-0 cursor-pointer">
        <span id={`${id}-label`} className="block text-sm font-bold text-torre-slate">{title}</span>
        <span id={`${id}-desc`} className="block text-xs text-slate-600">{desc}</span>
      </label>
      <Switch id={id} checked={checked} onCheckedChange={onChange} data-testid={id}
        aria-labelledby={`${id}-label`} aria-describedby={`${id}-desc`} />
    </div>
  );
}

export default function AccessibilityPanel({ open, onOpenChange, settings, update }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl bg-white max-h-[calc(100dvh-1.5rem)] overflow-y-auto overscroll-contain" data-testid="accessibility-panel"
        // Radio-group pattern: focus the selected theme, not the first option (a focus ring on
        // "Claro" while "Automático" was selected looked like two choices were active)
        onOpenAutoFocus={(e) => {
          const selected = e.currentTarget?.querySelector('[role="radio"][aria-checked="true"]');
          if (selected) { e.preventDefault(); selected.focus({ preventScroll: true }); }
        }}>
        <DialogHeader>
          <DialogTitle className="text-lg font-extrabold text-torre-slate">Accesibilidad y pantalla</DialogTitle>
          <DialogDescription className="text-sm text-slate-600">
            Ajusta SomosTorre a tu vista. Se guarda en este dispositivo.
          </DialogDescription>
        </DialogHeader>

        <fieldset>
          <legend className="text-xs font-bold uppercase tracking-wide text-slate-600 mb-2">Tema</legend>
          {/* Wraps instead of a fixed three columns: at a large phone text size "Automático" is wider
              than a third of the panel and ran over "Oscuro". The basis is in ch, which grows with
              the text, so the row breaks exactly when the words stop fitting. */}
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Tema">
            {THEMES.map(({ value, label, Icon }) => {
              const active = settings.theme === value;
              return (
                <button key={value} type="button" role="radio" aria-checked={active}
                  onClick={() => update({ theme: value })}
                  data-testid={`theme-${value}`}
                  className={`flex-1 basis-[11ch] min-w-0 flex flex-col items-center gap-1.5 rounded-xl border-2 px-2 py-3 text-sm font-bold text-center transition-colors ${
                    active ? 'border-torre-teal bg-torre-teal-light text-torre-teal-hover' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}>
                  <Icon size={20} aria-hidden="true" />
                  {label}
                </button>
              );
            })}
          </div>
          {settings.theme === 'system' && (
            <p className="mt-2 text-xs text-slate-600">Sigue el modo claro u oscuro de tu teléfono u ordenador.</p>
          )}
        </fieldset>

        <div className="divide-y divide-slate-100">
          <Row id="a11y-senior" Icon={Type} title="Texto grande (Modo Sénior)"
            desc="Letra y botones más grandes." checked={settings.seniorMode}
            onChange={(v) => update({ seniorMode: v })} />
          <Row id="a11y-contrast" Icon={Contrast} title="Alto contraste"
            desc="Textos y bordes más marcados, enlaces subrayados." checked={settings.highContrast}
            onChange={(v) => update({ highContrast: v })} />
          <Row id="a11y-motion" Icon={Zap} title="Reducir animaciones"
            desc="Sin transiciones ni movimiento en pantalla." checked={settings.reduceMotion}
            onChange={(v) => update({ reduceMotion: v })} />
        </div>

        <Link to="/accesibilidad" onClick={() => onOpenChange(false)}
          className="text-sm font-bold text-torre-teal hover:underline" data-testid="a11y-statement-link">
          Declaración de accesibilidad y cómo informar de un problema
        </Link>
      </DialogContent>
    </Dialog>
  );
}
